"use client";

import dynamic from "next/dynamic";
import type { ReactNode } from "react";
import { LazyMotion } from "framer-motion";
import { ThemeProvider } from "@/context/ThemeContext";

/**
 * Loads the `domMax` feature bundle (~30 KB gzipped) in its own chunk
 * after first paint. Components render `m.*` skeletons until features
 * resolve; animations begin once the chunk is available (one tick).
 */
const loadMotionFeatures = () =>
  import("@/lib/motion-features").then((m) => m.default);
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import { OrderProvider } from "@/context/OrderContext";
import { RecentlyViewedProvider } from "@/context/RecentlyViewedContext";
import { TutorialProvider } from "@/context/TutorialContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import type { CurrencyCode } from "@/lib/api/permalinks";
import { CartSidebar } from "@/components/cart/CartSidebar";
import { FloatingCartButton } from "@/components/cart/FloatingCartButton";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { ToastProvider } from "@/components/shared/Toast";

/**
 * Two heavy UI layers — the LiveKit voice agent and the first-visit
 * driver.js tutorial — are split into their own dynamic chunks so they
 * don't bloat the main bundle. They're loaded `ssr: false` because:
 *
 *   - The agent needs a live WebSocket + runtime WP config; rendering
 *     it on the server only emits an empty placeholder anyway.
 *   - The tutorial reads localStorage to decide visibility; SSR would
 *     produce hydration warnings.
 *
 * Both fall back to `null` while loading — there's nothing to flash.
 *
 * When `NEXT_PUBLIC_WEBSITE_AGENT=disabled` is set at build time, the
 * truthy branch below is dead code: webpack drops the `dynamic()` call
 * and the `import("@/components/AgentLayer")` literal entirely, so the
 * AgentLayer chunk (and its transitive `livekit-client` ~400 KB) is
 * never emitted or fetched on any page. The disabled fallback is just a
 * passthrough so the provider tree shape is unchanged.
 */
const AGENT_ENABLED = process.env.NEXT_PUBLIC_WEBSITE_AGENT !== "disabled";

const AgentLayer = AGENT_ENABLED
  ? dynamic(() => import("@/components/AgentLayer"), { ssr: false })
  : ({ children }: { children: ReactNode }) => <>{children}</>;

const TutorialLayer = dynamic(() => import("@/components/TutorialLayer"), {
  ssr: false,
  loading: () => null,
});

/**
 * Always-mounted shared chrome — cart, theme toggle. Cheap to hydrate
 * and needed everywhere, so they stay in the static client bundle.
 */
const SharedUI = ({ children }: { children: ReactNode }) => (
  <>
    {children}
    <CartSidebar />
    <FloatingCartButton />
    <ThemeToggle />
  </>
);

/**
 * Root client provider tree.
 *
 * Hierarchy intent:
 *   Theme → Toast → Auth → Order → Cart → RecentlyViewed → Tutorial
 *
 * The order matters where children read from outer providers:
 *   - Cart reads Auth (token-aware persistence)
 *   - Order reads Cart
 *   - Tutorial reads Cart (e.g. checking cart state before tour-step)
 *
 * Agent + Tutorial UI are mounted *inside* their providers via the
 * dynamic-imported layers above; the providers themselves stay here so
 * any descendant component can read their context synchronously without
 * waiting for a chunk to load.
 */
export function Providers({
  children,
  currency,
}: {
  children: ReactNode;
  currency: CurrencyCode;
}) {
  return (
    <LazyMotion features={loadMotionFeatures} strict>
      <ThemeProvider>
        <ToastProvider>
          <CurrencyProvider currency={currency}>
            <AuthProvider>
              <OrderProvider>
                <CartProvider>
                  <RecentlyViewedProvider>
                    <TutorialProvider>
                      <AgentLayer>
                        <SharedUI>{children}</SharedUI>
                      </AgentLayer>
                      <TutorialLayer />
                    </TutorialProvider>
                  </RecentlyViewedProvider>
                </CartProvider>
              </OrderProvider>
            </AuthProvider>
          </CurrencyProvider>
        </ToastProvider>
      </ThemeProvider>
    </LazyMotion>
  );
}
